import { ChangeDetectionStrategy, Component, inject, computed } from '@angular/core';
import { StickyFooterService } from './sticky-footer.service';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatDialog } from '@angular/material/dialog';
import { SidebarMenuService } from './sidebar-menu.service';
import { HeaderAdapterService } from './header-adapter.service';
import { LayoutRoutingService } from '../../../component-library/layout/layout-routing.service';
import { HeaderConfigStateService } from '../../../component-library/layout';
import { SidebarConfigStateService } from '../../../component-library/layout';
import { AppShellComponent } from '../../../component-library/layout';
import { EquifaxDisclosureAcknowledgementComponent } from '@organisations/equifax/equifax-disclosure-acknowledgement.component';
import { ActiveSecurityContextStateService } from '@security/active-security/active-security-context.state-service';
import { RightSidebarService } from '../right-sidebar/right-sidebar.service';
import { RightSidebarComponent } from '../right-sidebar/right-sidebar.component';
import { OrganisationMenuComponent } from '../header/organisation-menu/organisation-menu.component';
import { EmploymentMenuComponent } from '../header/employment-menu/employment-menu.component';
import { BureauMenuComponent } from '../header/bureau-menu/bureau-menu.component';
import { BannerService } from '@design/layout/banner-layout/banner.service';
import { DesignSystemModule } from '@design/design-system.module';
import { SalesModule } from '@app/sales/sales.module';
import { AuthStorageService } from '@auth-n/state/auth-storage.service';
import { EnforcePasskeyPolicyModalComponent } from '@security/users/passkeys/enforce-passkey-policy-modal/enforce-passkey-policy-modal.component';
import { DomainMigrationModalComponent } from '@app/domain-migration/domain-migration-modal/domain-migration-modal.component';

@UntilDestroy()
@Component({
  selector: 'app-new-layout',
  templateUrl: './new-app-layout.component.html',
  styleUrls: ['./new-app-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    NgClass,
    NgTemplateOutlet,
    RouterOutlet,
    AppShellComponent,
    RightSidebarComponent,
    OrganisationMenuComponent,
    EmploymentMenuComponent,
    BureauMenuComponent,
    DesignSystemModule,
    SalesModule,
    EnforcePasskeyPolicyModalComponent,
    DomainMigrationModalComponent
  ]
})
export class NewAppLayoutComponent {
  private sidebarMenuAdapter = inject(SidebarMenuService);
  private headerAdapter = inject(HeaderAdapterService);
  private layoutRouting = inject(LayoutRoutingService);
  private dialog = inject(MatDialog);
  bannerService = inject(BannerService);
  rightSidebarService = inject(RightSidebarService);
  headerConfigState = inject(HeaderConfigStateService);
  sidebarConfigState = inject(SidebarConfigStateService);
  stickyFooterService = inject(StickyFooterService);
  footerTpl = computed(() => this.stickyFooterService.footerTemplate());
  activeSecurity = inject(ActiveSecurityContextStateService);
  authStorageService = inject(AuthStorageService);

  onHrNavigationRequested(hrUrl: string): void {
    const employmentResolved = this.activeSecurity.tryResolveActiveEmployment(true, true);

    if (!employmentResolved) {
      // If employment couldn't be resolved, make sure organisation cookies for the current active organisation
      const activeMembership = this.activeSecurity.activeMembership;
      if (activeMembership) {
        this.authStorageService.setOrganisation(activeMembership.organisationId, activeMembership.organisationName);
      }
    }

    window.location.href = hrUrl;
  }

  /** The passkey modal outranks the domain migration one, which is held back until it closes -- show it now. */
  onPasskeyModalClosed(): void {
    this.activeSecurity.showDomainMigrationModalIfPending();
  }

  constructor() {
    this.layoutRouting.setAngularMode(true);

    this.sidebarMenuAdapter
      .getSidebarMenuConfig$()
      .pipe(untilDestroyed(this))
      .subscribe((config) => this.sidebarConfigState.setSidebarConfig(config));

    this.headerAdapter
      .getHeaderConfig$()
      .pipe(untilDestroyed(this))
      .subscribe((config) => this.headerConfigState.setHeaderConfig(config));

    this.activeSecurity.activeMembership$.pipe(untilDestroyed(this)).subscribe((activeMembership) => {
      if (activeMembership && this.shouldShowEquifaxDisclosureDialog(activeMembership.hasAcknowledgedEquifaxDisclosure)) {
        this.dialog.open(EquifaxDisclosureAcknowledgementComponent, {
          panelClass: 'equifax-disclosure-acknowledgement-modal-panel'
        });
      }
    });
  }

  private shouldShowEquifaxDisclosureDialog(hasAcknowledgedEquifaxDisclosure?: boolean): boolean {
    return (
      this.activeSecurity.isCintraEmployee() === false &&
      this.activeSecurity.hasBureauProfilePermissions() === false &&
      this.activeSecurity.hasOrganisationAuthorityTo('EquifaxDataConsent') &&
      hasAcknowledgedEquifaxDisclosure === false
    );
  }
}
